import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Autocomplete,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useUser } from "../../Context/UserContext";
import DynamicTable from "../Dynamic/DynamicTable";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import { toast } from "react-toastify";
import { GetApi, PostApi, DeleteApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

const canModify = (role) => role === "district_admin";

const now = new Date();
const baseYear =
  now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const currentFY = `${baseYear}-${baseYear + 1}`;
const years = Array.from(
  { length: 10 },
  (_, i) => `${baseYear + i}-${baseYear + i + 1}`,
);

const BlockTarget = () => {
  const { token, role, user, authData, userId } = useUser();

  // --- Data States ---
  const [data, setData] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [districtTargetLimits, setDistrictTargetLimits] = useState([]);
  const [masterDepartments, setMasterDepartments] = useState([]);
  const [masterDistricts, setMasterDistricts] = useState([]);

  // --- UI States ---
  const [actualDistrictId, setActualDistrictId] = useState(null);
  const [userDistrictName, setUserDistrictName] = useState("Loading...");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [overallBalance, setOverallBalance] = useState(0);

  // Detailed pending items
  const [pendingDetails, setPendingDetails] = useState([]);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [existModal, setExistModal] = useState(false);
  const [existInfo, setExistInfo] = useState(null);

  // --- Filter States ---
  const [filterDepartment, setFilterDepartment] = useState(null);
  const [filterDistrict, setFilterDistrict] = useState(null);
  const [filterBlock, setFilterBlock] = useState(null);
  const [filterScheme, setFilterScheme] = useState(null);

  // --- Form States ---
  const [form, setForm] = useState({
    department_id: null,
    block_id: null,
    financial_year: currentFY,
    scheme_type: null,
    scheme_id: null,
    target_quantity: "",
  });

  const [errors, setErrors] = useState({});
  const hasModifyPermission = canModify(role);

  // Filter Visibility Logic
  const showDeptFilter = role === "superadmin";
  const showDistFilter = role === "superadmin" || role === "department_admin";
  const showBlockFilter =
    role === "superadmin" ||
    role === "department_admin" ||
    role === "district_admin";

  // ================= 1. FETCH DATA =================
  const fetchTargets = async () => {
    try {
      const res = await GetApi(
        `${API_URLS.BlockTarget}?user_id=${userId}`,
        token,
      );
      if (res && res.success) {
        const freshData = res.data || [];
        setData(freshData);
        return freshData;
      }
      setData([]);
      return [];
    } catch (err) {
      console.error("Fetch Targets Error:", err);
      setData([]);
      return [];
    }
  };

  const fetchInitialData = async () => {
    try {
      const [blockRes, schemeRes, distLimitsRes, deptRes, distRes] =
        await Promise.all([
          GetApi(
            `${API_URLS.GetBlocks}?district_id=${authData?.district_id}`,
            token,
          ),
          GetApi(API_URLS.Schemes, token),
          GetApi(`${API_URLS.DistrictTarget}?user_id=${userId}`, token),
          GetApi(API_URLS.Department, token),
          GetApi(API_URLS.GetDistricts, token),
        ]);

      const rawBlocks =
        blockRes?.data ||
        blockRes?.blocks ||
        (Array.isArray(blockRes) ? blockRes : []);
      // ✅ Sort Blocks Alphabetically
      const sortedBlocks = rawBlocks.sort((a, b) =>
        (a.Block_Name || "").localeCompare(b.Block_Name || ""),
      );
      setBlocks(
        sortedBlocks.map((b) => ({
          id: b.id || b.block_id,
          Block_Name: b.Block_Name || b.block_name,
        })),
      );

      setSchemes(schemeRes?.data || schemeRes || []);
      setMasterDepartments(deptRes?.data || deptRes || []);
      setMasterDistricts(distRes?.data || distRes || []);

      setActualDistrictId(authData?.district_id);
      setUserDistrictName(authData?.district_name || "My District");

      const limits = distLimitsRes?.data || distLimitsRes || [];
      setDistrictTargetLimits(limits);

      calculateOverallBalance(limits, [], form.financial_year);
    } catch (err) {
      console.error("Setup Error:", err);
    }
  };

  // ================= 2. OVERALL BALANCE LOGIC =================
  const calculateOverallBalance = (
    currentLimits = districtTargetLimits,
    currentData = data,
    selectedYearStr = currentFY,
  ) => {
    let totalBalance = 0;
    const details = [];

    const selectedYear = Number(selectedYearStr.split("-")[0]);

    currentLimits.forEach((lim) => {
      if (!lim.start_date) return;
      const limYear = new Date(lim.start_date).getFullYear();

      if (limYear !== selectedYear) return;

      const dbUsed = currentData.reduce((sum, row) => {
        if (!row.start_date) return sum;
        const rowYear = new Date(row.start_date).getFullYear();

        if (rowYear !== selectedYear) return sum;
        if (
          Number(row.target_department_id) !== Number(lim.target_department_id)
        )
          return sum;

        if (lim.scheme_type === "Scheme") {
          if (Number(row.scheme_id) === Number(lim.scheme_id))
            return sum + Number(row.target_quantity || 0);
        } else {
          if (row.scheme_type === "Non-Scheme")
            return sum + Number(row.target_quantity || 0);
        }
        return sum;
      }, 0);

      const balance = Number(lim.target_quantity) - dbUsed;

      if (balance !== 0) {
        totalBalance += balance;
        details.push({
          id: lim.id || lim.target_id,
          department_name: lim.department_name || "Department",
          scheme_name:
            lim.scheme_type === "Scheme" ? lim.scheme_name : "Non-Scheme",
          balance: balance,
          isOverAssigned: balance < 0,
        });
      }
    });

    setOverallBalance(totalBalance);
    setPendingDetails(details);
  };

  useEffect(() => {
    if (token && userId) {
      fetchTargets().then((freshData) => {
        if (districtTargetLimits.length > 0) {
          calculateOverallBalance(
            districtTargetLimits,
            freshData,
            form.financial_year,
          );
        }
      });
      fetchInitialData();
    }
  }, [token, userId]);

  useEffect(() => {
    calculateOverallBalance(districtTargetLimits, data, form.financial_year);
  }, [data, districtTargetLimits, form.financial_year]);

  // ================= 3. RESTRICTION LOGIC =================
  const availableDepartments = useMemo(() => {
    const uniqueDepts = {};
    (districtTargetLimits || []).forEach((lim) => {
      if (lim.target_department_id && lim.department_name) {
        uniqueDepts[lim.target_department_id] = lim.department_name;
      }
    });
    return Object.entries(uniqueDepts).map(([id, name]) => ({
      id: Number(id),
      name,
    }));
  }, [districtTargetLimits]);

  const displayedDistLimits = useMemo(() => {
    if (!form.financial_year || !form.department_id) return [];
    return districtTargetLimits.filter((lim) => {
      const limYear = lim.start_date
        ? `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}`
        : null;
      return (
        limYear === form.financial_year &&
        Number(lim.target_department_id) === Number(form.department_id)
      );
    });
  }, [districtTargetLimits, form.financial_year, form.department_id]);

  const distAllowedTypes = useMemo(() => {
    const types = new Set(displayedDistLimits.map((l) => l.scheme_type));
    if (types.has("Non-Scheme") && types.has("Scheme"))
      return ["Non-Scheme", "Scheme"];
    if (types.has("Non-Scheme")) return ["Non-Scheme"];
    if (types.has("Scheme")) return ["Scheme"];
    return [];
  }, [displayedDistLimits]);

  const distAllowedSchemeIds = useMemo(() => {
    return displayedDistLimits
      .filter((l) => l.scheme_type === "Scheme")
      .map((l) => Number(l.scheme_id));
  }, [displayedDistLimits]);

  // ================= 4. BLOCK HIERARCHY LOGIC =================
  const getExistingForBlockYear = (
    targetData,
    blockId,
    fy,
    excludeEditId = null,
  ) => {
    return targetData.filter((item) => {
      const isEditItem =
        excludeEditId !== null && String(item.id) === String(excludeEditId);
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      return (
        Number(item.block_id) === Number(blockId) &&
        itemYear === fy &&
        !isEditItem
      );
    });
  };

  const isBlockComplete = (blockId, fy, excludeEditId = null) => {
    if (distAllowedTypes.length === 0) return true;
    const existing = getExistingForBlockYear(data, blockId, fy, excludeEditId);

    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));

    const nonSchemeDone = distAllowedTypes.includes("Non-Scheme")
      ? hasNonScheme
      : true;
    const schemesDone = distAllowedTypes.includes("Scheme")
      ? distAllowedSchemeIds.every((s) => usedSchemeIds.includes(s))
      : true;

    return nonSchemeDone && schemesDone;
  };

  const availableBlocks = useMemo(() => {
    if (!form.financial_year || !form.department_id) return [];
    return blocks.filter(
      (block) => !isBlockComplete(block.id, form.financial_year, editId),
    );
  }, [
    data,
    form.financial_year,
    form.department_id,
    blocks,
    distAllowedTypes,
    distAllowedSchemeIds,
    editId,
  ]);

  const availableTypes = useMemo(() => {
    if (distAllowedTypes.length === 0 || !form.block_id || !form.financial_year)
      return [];
    const existing = getExistingForBlockYear(
      data,
      form.block_id,
      form.financial_year,
      editId,
    );

    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    const hasRemainingSchemes = distAllowedSchemeIds.some(
      (s) => !usedSchemeIds.includes(s),
    );

    const finalTypes = [];
    if (distAllowedTypes.includes("Non-Scheme") && !hasNonScheme)
      finalTypes.push("Non-Scheme");
    if (distAllowedTypes.includes("Scheme") && hasRemainingSchemes)
      finalTypes.push("Scheme");
    return finalTypes;
  }, [
    data,
    form.block_id,
    form.financial_year,
    editId,
    distAllowedTypes,
    distAllowedSchemeIds,
  ]);

  const availableSchemes = useMemo(() => {
    if (form.scheme_type !== "Scheme" || !form.block_id || !form.financial_year)
      return [];
    const existing = getExistingForBlockYear(
      data,
      form.block_id,
      form.financial_year,
      editId,
    );
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));

    return schemes.filter(
      (s) =>
        distAllowedSchemeIds.includes(Number(s.id)) &&
        !usedSchemeIds.includes(Number(s.id)),
    );
  }, [
    data,
    form.block_id,
    form.financial_year,
    editId,
    form.scheme_type,
    schemes,
    distAllowedSchemeIds,
  ]);

  const hierarchyStatus = useMemo(() => {
    if (!form.block_id || !form.financial_year) return null;
    const existing = getExistingForBlockYear(
      data,
      form.block_id,
      form.financial_year,
      null,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    return {
      nonSchemeSet: hasNonScheme,
      schemesSet: usedSchemeIds.length,
      totalSchemes: distAllowedSchemeIds.length,
      remainingSchemes: distAllowedSchemeIds.length - usedSchemeIds.length,
    };
  }, [form.block_id, form.financial_year, data, distAllowedSchemeIds]);

  useEffect(() => {
    if (form.scheme_type && !availableTypes.includes(form.scheme_type)) {
      setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
    }
  }, [availableTypes, form.scheme_type]);

  useEffect(() => {
    if (form.scheme_type === "Scheme" && form.scheme_id) {
      if (
        !availableSchemes.some((s) => Number(s.id) === Number(form.scheme_id))
      ) {
        setForm((prev) => ({ ...prev, scheme_id: null }));
      }
    }
  }, [availableSchemes, form.scheme_type, form.scheme_id]);

  useEffect(() => {
    if (form.block_id && !availableBlocks.some((b) => b.id === form.block_id)) {
      setForm((prev) => ({
        ...prev,
        block_id: null,
        scheme_type: null,
        scheme_id: null,
      }));
    }
  }, [availableBlocks, form.block_id]);

  useEffect(() => {
    if (
      form.department_id &&
      !availableDepartments.some((d) => d.id === form.department_id)
    ) {
      setForm((prev) => ({
        ...prev,
        department_id: null,
        block_id: null,
        scheme_type: null,
        scheme_id: null,
      }));
    }
  }, [availableDepartments, form.department_id]);

  // ================= 5. LIMIT CALCULATION =================
  const getMaxAllowed = () => {
    if (!form.financial_year || !form.scheme_type || !form.department_id)
      return null;
    const isScheme = form.scheme_type === "Scheme";
    if (isScheme && !form.scheme_id) return null;

    let totalAllowed = 0;
    if (isScheme) {
      const masterLimit = districtTargetLimits.find(
        (lim) =>
          Number(lim.scheme_id) === Number(form.scheme_id) &&
          Number(lim.target_department_id) === Number(form.department_id) &&
          lim.start_date &&
          `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}` ===
            form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    } else {
      const masterLimit = districtTargetLimits.find(
        (lim) =>
          lim.scheme_type === "Non-Scheme" &&
          Number(lim.target_department_id) === Number(form.department_id) &&
          lim.start_date &&
          `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}` ===
            form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    }

    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      const rowYear = `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`;
      if (rowYear !== form.financial_year) return sum;
      if (editId !== null && String(row.id) === String(editId)) return sum;
      if (Number(row.target_department_id) !== Number(form.department_id))
        return sum;

      if (isScheme) {
        if (Number(row.scheme_id) === Number(form.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);

    const maxAllowed = totalAllowed - dbUsed;
    return maxAllowed;
  };

  const getDistRemainingBalance = (lim) => {
    const limYear = lim.start_date
      ? `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}`
      : null;
    if (limYear !== form.financial_year) return Number(lim.target_quantity);

    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      const rowYear = `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`;
      if (rowYear !== form.financial_year) return sum;
      if (editId !== null && String(row.id) === String(editId)) return sum;
      if (Number(row.target_department_id) !== Number(lim.target_department_id))
        return sum;

      if (lim.scheme_type === "Scheme") {
        if (Number(row.scheme_id) === Number(lim.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);
    return Number(lim.target_quantity) - dbUsed;
  };

  // ================= 6. FILTER LOGIC & SORTING =================
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let result = data.filter((row) => {
      const matchDept =
        !filterDepartment ||
        Number(row.target_department_id) === Number(filterDepartment.id);
      const matchDist =
        !filterDistrict ||
        Number(row.district_id) === Number(filterDistrict.id);
      const matchBlock =
        !filterBlock || Number(row.block_id) === Number(filterBlock.id);
      let matchScheme = true;
      if (filterScheme) {
        if (filterScheme === "Non-Scheme") {
          matchScheme = row.scheme_type === "Non-Scheme";
        } else {
          matchScheme =
            row.scheme_type === "Scheme" &&
            String(row.scheme_id) === String(filterScheme.id);
        }
      }
      return matchDept && matchDist && matchBlock && matchScheme;
    });

    // ✅ SORT TABLE DATA BY BLOCK NAME (ASCENDING)
    return result.sort((a, b) => {
      const nameA = (a.block_name || "").toString().toLowerCase();
      const nameB = (b.block_name || "").toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data, filterDepartment, filterDistrict, filterBlock, filterScheme]);

  // ✅ CALCULATE TOTAL TARGET VALUE
  const totalTargetValue = useMemo(() => {
    return filteredData.reduce(
      (sum, row) => sum + Number(row.target_quantity || 0),
      0,
    );
  }, [filteredData]);

  // ================= 7. FORM HELPERS & VALIDATION =================
  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
  };

  const resetHierarchy = () => {
    setForm((prev) => ({
      ...prev,
      block_id: null,
      scheme_type: null,
      scheme_id: null,
    }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.block_id;
      delete n.scheme_type;
      delete n.scheme_id;
      return n;
    });
  };

  const resetTypeAndScheme = () => {
    setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.scheme_type;
      delete n.scheme_id;
      return n;
    });
  };

  const validate = () => {
    const tempErrors = {};
    if (!form.department_id) tempErrors.department_id = "Select a department";
    if (!form.block_id) tempErrors.block_id = "Select a block";
    if (!form.financial_year)
      tempErrors.financial_year = "Select financial year";
    if (!form.scheme_type) tempErrors.scheme_type = "Select a target type";
    if (form.scheme_type === "Scheme" && !form.scheme_id)
      tempErrors.scheme_id = "Select a scheme";
    if (!form.target_quantity || Number(form.target_quantity) <= 0)
      tempErrors.target_quantity = "Enter a valid quantity";

    const max = getMaxAllowed();
    if (
      !tempErrors.target_quantity &&
      max !== null &&
      max < Number(form.target_quantity)
    ) {
      tempErrors.target_quantity =
        max < 0
          ? `Dept limit exceeded by ${Math.abs(max).toLocaleString()}. Delete block targets first.`
          : `Exceeds available limit (${max.toLocaleString()})`;
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // ================= 8. CRUD ACTIONS =================
  const handleSubmit = async () => {
    if (!validate()) return;

    const duplicate = data.find((item) => {
      if (editId && String(item.id) === String(editId)) return false;
      if (Number(item.block_id) !== Number(form.block_id)) return false;
      if (Number(item.target_department_id) !== Number(form.department_id))
        return false;
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      if (itemYear !== form.financial_year) return false;
      if (form.scheme_type === "Scheme") {
        return (
          item.scheme_type === "Scheme" &&
          Number(item.scheme_id) === Number(form.scheme_id)
        );
      }
      return item.scheme_type === "Non-Scheme";
    });

    if (duplicate) {
      const blockName =
        blocks.find((b) => b.id === form.block_id)?.Block_Name || "Block";
      const typeName =
        form.scheme_type === "Scheme"
          ? schemes.find((s) => s.id === form.scheme_id)?.name || "Scheme"
          : "Non-Scheme";
      setExistInfo({ block: blockName, type: typeName });
      setExistModal(true);
      return;
    }

    try {
      if (editId) {
        await DeleteApi(`${API_URLS.BlockTarget}/${editId}`, {}, token);
      }

      const fyStart = form.financial_year.split("-")[0];
      const payload = {
        block_id: Number(form.block_id),
        district_id: Number(actualDistrictId),
        target_department_id: Number(form.department_id),
        target_quantity: Number(form.target_quantity),
        start_date: `${fyStart}-04-01`,
        end_date: `${Number(fyStart) + 1}-03-31`,
        scheme_type: form.scheme_type,
        scheme_id:
          form.scheme_type === "Scheme" ? Number(form.scheme_id) : null,
      };

      await PostApi(API_URLS.BlockTarget, payload, token);
      const freshData = await fetchTargets();

      const isComplete = isBlockComplete(form.block_id, form.financial_year);

      if (!isComplete) {
        const existing = getExistingForBlockYear(
          freshData,
          form.block_id,
          form.financial_year,
        );
        const hasNonScheme = existing.some(
          (t) => t.scheme_type === "Non-Scheme",
        );
        const usedSchemeIds = existing
          .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
          .map((t) => Number(t.scheme_id));
        const hasRemainingSchemes = distAllowedSchemeIds.some(
          (s) => !usedSchemeIds.includes(s),
        );

        const nextType =
          distAllowedTypes.includes("Non-Scheme") && !hasNonScheme
            ? "Non-Scheme"
            : distAllowedTypes.includes("Scheme") && hasRemainingSchemes
              ? "Scheme"
              : null;

        setForm((prev) => ({
          ...prev,
          scheme_type: nextType,
          scheme_id: null,
          target_quantity: "",
        }));
        setErrors({});
        toast.success("Target saved! More targets pending for this block.");
      } else {
        handleClose();
        toast.success("All targets set for this block!");
      }
    } catch (err) {
      console.error("Save Error:", err);
      toast.error(err.response?.data?.message || "Failed to save target");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    const itemYear = row.start_date
      ? `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`
      : currentFY;
    setForm({
      department_id: row.target_department_id,
      block_id: row.block_id,
      financial_year: itemYear,
      scheme_type: row.scheme_type || (row.scheme_id ? "Scheme" : "Non-Scheme"),
      scheme_id: row.scheme_id || null,
      target_quantity: row.target_quantity,
    });
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({
      department_id: null,
      block_id: null,
      financial_year: currentFY,
      scheme_type: null,
      scheme_id: null,
      target_quantity: "",
    });
    setErrors({});
  };

  const confirmDelete = async () => {
    try {
      await DeleteApi(`${API_URLS.BlockTarget}/${deleteTarget.id}`, {}, token);
      toast.success("Target Deleted");
      fetchTargets();
    } catch {
      toast.error("Delete Failed");
    } finally {
      setDeleteModal(false);
    }
  };

  const columns = [
    {
      field: "department_name",
      headerName: "Department",
      render: (r) => r.department_name || "—",
    },
    { field: "district_name", headerName: "District" },
    { field: "block_name", headerName: "Block" },
    { field: "scheme_type", headerName: "Type" },
    {
      field: "scheme_name",
      headerName: "Scheme",
      render: (r) => r.scheme_name || "—",
    },
    { field: "target_quantity", headerName: "Target Value" },
    {
      field: "start_date",
      headerName: "FY",
      render: (r) =>
        r.start_date
          ? `${new Date(r.start_date).getFullYear()}-${new Date(r.start_date).getFullYear() + 1}`
          : "—",
    },
  ];

  // ================= 9. RENDER =================
  return (
    <Box p={3}>
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 1, border: "1px solid #eee", borderRadius: 2 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" spacing={2} flex={1} alignItems="center">
            <FilterListIcon sx={{ color: "#2e7d32" }} />

            {showDeptFilter && (
              <Autocomplete
                options={masterDepartments}
                getOptionLabel={(opt) => opt.name}
                value={filterDepartment}
                onChange={(e, val) => setFilterDepartment(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 220 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    placeholder="Search..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000000",
                      },
                    }}
                  />
                )}
              />
            )}

            {showDistFilter && (
              <Autocomplete
                options={masterDistricts}
                getOptionLabel={(opt) => opt.District_Name}
                value={filterDistrict}
                onChange={(e, val) => setFilterDistrict(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 220 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="District"
                    placeholder="Search..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000000",
                      },
                    }}
                  />
                )}
              />
            )}

            {showBlockFilter && (
              <Autocomplete
                options={blocks}
                getOptionLabel={(opt) => opt.Block_Name}
                value={filterBlock}
                onChange={(e, val) => setFilterBlock(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 220 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Block"
                    placeholder="Search..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000000",
                      },
                    }}
                  />
                )}
              />
            )}

            <Autocomplete
              options={[{ id: "Non-Scheme", name: "Non-Scheme" }, ...schemes]}
              getOptionLabel={(opt) => opt.name}
              value={filterScheme}
              onChange={(e, val) => setFilterScheme(val)}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              size="small"
              sx={{ minWidth: 250 }}
              clearIcon={<ClearIcon fontSize="small" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scheme / Type"
                  placeholder="Search..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                        borderWidth: 1.5,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2e7d32 !important",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1b5e20",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000000",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#000000",
                    },
                  }}
                />
              )}
            />
          </Stack>

          {/* Conditional Rendering for Target Button / Status Chip */}
          <Stack direction="row" spacing={2} alignItems="center">
            {hasModifyPermission && overallBalance !== 0 && (
              <>
                <Chip
                  icon={<PendingActionsIcon style={{ fontSize: 18 }} />}
                  label={`${overallBalance.toLocaleString()} ${overallBalance < 0 ? "Over-Assigned" : "Pending"}`}
                  color={overallBalance < 0 ? "error" : "warning"}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderColor: overallBalance < 0 ? "red" : "orange",
                    color: overallBalance < 0 ? "#b71c1c" : "#e65100",
                  }}
                />
                {overallBalance > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setOpen(true)}
                    sx={{ fontWeight: 600 }}
                  >
                    Set Target
                  </Button>
                )}
              </>
            )}

            {hasModifyPermission && overallBalance === 0 && (
              <Chip
                icon={<TaskAltIcon style={{ fontSize: 18 }} />}
                label="All Targets Completed"
                color="success"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  border: "1px solid #a5d6a7",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ✅ UPDATED: Breakdown now shows RED if negative remainder exists */}
      {hasModifyPermission && pendingDetails.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: pendingDetails.some((d) => d.isOverAssigned)
              ? "1px dashed #ef9a9a"
              : "1px dashed #ffcc80",
            bgcolor: pendingDetails.some((d) => d.isOverAssigned)
              ? "#ffebee"
              : "#fffde7",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            {pendingDetails.some((d) => d.isOverAssigned) ? (
              <ErrorOutlineIcon sx={{ fontSize: 18, color: "#b71c1c" }} />
            ) : (
              <PendingActionsIcon sx={{ fontSize: 18, color: "#e65100" }} />
            )}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: pendingDetails.some((d) => d.isOverAssigned)
                  ? "#b71c1c"
                  : "#e65100",
              }}
            >
              {pendingDetails.some((d) => d.isOverAssigned)
                ? "Over-Assigned Breakdown"
                : "Pending Breakdown"}{" "}
              ({form.financial_year})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} useFlexGap>
            {pendingDetails.map((det) => (
              <Chip
                key={det.id}
                icon={
                  det.isOverAssigned ? (
                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <LocalFloristIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={`${det.department_name} - ${det.scheme_name}: ${det.balance.toLocaleString()}`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: det.isOverAssigned ? "#e57373" : "#ffb74d",
                  bgcolor: "#ffffff",
                  color: det.isOverAssigned ? "#b71c1c" : "#bf360c",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: det.isOverAssigned ? "#d32f2f" : "#e65100",
                  },
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* ✅ TOTAL TARGET VALUE DISPLAY ABOVE TABLE */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #c8e6c9",
          borderRadius: 2,
          bgcolor: "#f1f8e9",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Total Target Value
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b5e20" }}>
            {totalTargetValue.toLocaleString()}
          </Typography>
        </Stack>
      </Paper>

      <DynamicTable
        data={filteredData}
        columns={columns}
        onEdit={hasModifyPermission ? handleEdit : undefined}
        onDelete={
          hasModifyPermission
            ? (r) => {
                setDeleteTarget(r);
                setDeleteModal(true);
              }
            : undefined
        }
      />

      <Dialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography fontWeight={600}>Confirm Delete</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this target?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={existModal}
        onClose={() => setExistModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight={600}>Target Already Exists</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Target for <strong>{existInfo?.block}</strong> with{" "}
            <strong>{existInfo?.type}</strong> is already set for this financial
            year.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExistModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DynamicOffcanvas
        open={open}
        title={editId ? "Edit Target" : "Set Block Target"}
        onClose={handleClose}
        actions={
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editId ? "Update" : "Save Target"}
            </Button>
          </Stack>
        }
      >
        {displayedDistLimits.length > 0 && (
          <Alert
            severity={
              displayedDistLimits.some((l) => getDistRemainingBalance(l) < 0)
                ? "error"
                : "info"
            }
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              District Targets ({form.financial_year})
            </AlertTitle>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {displayedDistLimits.map((lim) => {
                const bal = getDistRemainingBalance(lim);
                return (
                  <Typography
                    key={lim.id}
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: bal < 0 ? "error.main" : "text.primary",
                    }}
                  >
                    <span>
                      {lim.scheme_type === "Scheme"
                        ? lim.scheme_name
                        : "Non-Scheme"}
                      :
                    </span>
                    <strong>
                      {bal.toLocaleString()}{" "}
                      {bal < 0 ? "(Over-assigned)" : "left"}
                    </strong>
                  </Typography>
                );
              })}
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color:
                    displayedDistLimits.reduce(
                      (acc, curr) => acc + getDistRemainingBalance(curr),
                      0,
                    ) < 0
                      ? "error.main"
                      : "text.primary",
                }}
              >
                <span>Overall Remaining:</span>
                <strong>
                  {displayedDistLimits
                    .reduce(
                      (acc, curr) => acc + getDistRemainingBalance(curr),
                      0,
                    )
                    .toLocaleString()}
                </strong>
              </Typography>
            </Stack>
          </Alert>
        )}

        {hierarchyStatus && (
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              Block Allocation Progress
            </AlertTitle>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              {distAllowedTypes.includes("Non-Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.nonSchemeSet ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">Non-Scheme</Typography>
                </Stack>
              )}
              {distAllowedTypes.includes("Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.remainingSchemes === 0 ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">
                    Schemes ({hierarchyStatus.schemesSet}/
                    {hierarchyStatus.totalSchemes})
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              label="District"
              value={userDistrictName}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableDepartments}
              getOptionLabel={(opt) => opt.name}
              value={
                availableDepartments.find((d) => d.id === form.department_id) ||
                null
              }
              onChange={(e, newValue) => {
                handleChange("department_id", newValue?.id || null);
                resetHierarchy();
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  placeholder="Select department..."
                  error={!!errors.department_id}
                  helperText={
                    errors.department_id ||
                    `${availableDepartments.length} dept(s) available`
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={years}
              getOptionLabel={(opt) => opt}
              value={form.financial_year || null}
              onChange={(e, newValue) => {
                handleChange("financial_year", newValue || null);
                resetHierarchy();
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Financial Year"
                  placeholder="Select year..."
                  error={!!errors.financial_year}
                  helperText={errors.financial_year}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableBlocks}
              getOptionLabel={(opt) => opt.Block_Name}
              value={blocks.find((b) => b.id === form.block_id) || null}
              onChange={(e, newValue) => {
                handleChange("block_id", newValue?.id || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              disabled={!form.financial_year || !form.department_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Block"
                  placeholder="Search block..."
                  error={!!errors.block_id}
                  helperText={
                    errors.block_id ||
                    (!form.department_id
                      ? "Select department first"
                      : `${availableBlocks.length} block(s) available`)
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableTypes}
              getOptionLabel={(opt) => opt}
              value={form.scheme_type || null}
              onChange={(e, newValue) => {
                handleChange("scheme_type", newValue || null);
                handleChange("scheme_id", null);
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              disabled={!form.block_id || !form.financial_year}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Type"
                  placeholder="Select type..."
                  error={!!errors.scheme_type}
                  helperText={
                    errors.scheme_type ||
                    (!form.block_id ? "Select block first" : "")
                  }
                />
              )}
            />
          </Grid>

          {form.scheme_type === "Scheme" && (
            <Grid item xs={12} width="250px">
              <Autocomplete
                options={availableSchemes}
                getOptionLabel={(opt) => opt.name}
                value={
                  schemes.find(
                    (s) => Number(s.id) === Number(form.scheme_id),
                  ) || null
                }
                onChange={(e, newValue) =>
                  handleChange("scheme_id", newValue?.id || null)
                }
                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v?.id)}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Scheme"
                    placeholder="Search scheme..."
                    error={!!errors.scheme_id}
                    helperText={
                      errors.scheme_id ||
                      `${availableSchemes.length} scheme(s) remaining`
                    }
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Target Quantity"
              placeholder="Enter quantity..."
              value={form.target_quantity}
              onChange={(e) => {
                let val = e.target.value;
                val = val.replace(/[eE+-]/g, "");
                if (val === "-") val = "";
                handleChange("target_quantity", val);

                const maxLimit = getMaxAllowed();
                if (maxLimit !== null && val !== "" && Number(val) > maxLimit) {
                  setErrors((prev) => ({
                    ...prev,
                    target_quantity:
                      maxLimit < 0
                        ? `Dept limit exceeded by ${Math.abs(maxLimit).toLocaleString()}. Delete block targets first.`
                        : `Max limit is ${maxLimit.toLocaleString()}`,
                  }));
                } else if (
                  errors.target_quantity &&
                  (val === "" || Number(val) <= (maxLimit || 0))
                ) {
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.target_quantity;
                    return n;
                  });
                }
              }}
              inputProps={{ min: 0 }}
              error={!!errors.target_quantity}
              helperText={
                errors.target_quantity ||
                (getMaxAllowed() !== null
                  ? getMaxAllowed() < 0
                    ? `Limit exceeded by ${Math.abs(getMaxAllowed()).toLocaleString()}`
                    : `Max Limit: ${getMaxAllowed().toLocaleString()}`
                  : "Select scheme/type to see limit")
              }
            />
          </Grid>
        </Grid>
      </DynamicOffcanvas>
    </Box>
  );
};

export default BlockTarget;

